@import 'core';

/**
 * @prop --ads-checkbox-background-color: Default background color
 * @prop --ads-checkbox-background-color-disabled: Disabled background color
 * @prop --ads-checkbox-background-color-readonly: Readonly background color
 *
 * @prop --ads-checkbox-border-color: Default border color
 * @prop --ads-checkbox-border-color-checked: Checked border color
 * @prop --ads-checkbox-border-color-disabled: Disabled border color
 * @prop --ads-checkbox-border-color-readonly: Readonly border color
 *
 * @prop --ads-checkbox-cross-color-checked: Default cross color
 * @prop --ads-checkbox-cross-color-hover: Hovered cross color
 * @prop --ads-checkbox-cross-color-disabled: Disabled cross color
 * @prop --ads-checkbox-cross-color-readonly: Readonly cross color
 *
 * @prop --ads-checkbox-margin-left: Default margin-left
 * @prop --ads-checkbox-margin-right: Default margin-right
 *
 * @prop --ads-checkbox-helper-offset-margin: Helper offset margin
 * @prop --ads-checkbox-helper-offset-small-margin: Helper small offset margin
 *
 * @prop --ads-checkbox-label-color: Default label color
 * @prop --ads-checkbox-label-color-hover: Hovered label color
 * @prop --ads-checkbox-label-color-required: Required label color
 * @prop --ads-checkbox-label-margin-left: Default label margin-left
 * @prop --ads-checkbox-label-margin-right: Default label margin-right
 * @prop --ads-checkbox-label-text-align: Default label text-align
 */

$ads-checkbox-border-color: #a3a8ae;
$ads-checkbox-size-default: 22px;
$ads-checkbox-size-small: 16px;

:host {
  display: inline-block;
}

:host([theme='default']) .c-checkbox {
  // Background

  --ads-checkbox-background-color: #{$ads-checkbox-background-color};
  --ads-checkbox-background-color-disabled: #{$ads-checkbox-background-color-disabled};
  --ads-checkbox-background-color-readonly: #{$ads-checkbox-background-color-readonly};

  // Border

  --ads-checkbox-border-color: #{$ads-checkbox-border-color};
  --ads-checkbox-border-color-checked: #{$ads-checkbox-border-color-checked};
  --ads-checkbox-border-color-disabled: #{$ads-checkbox-border-color-disabled};
  --ads-checkbox-border-color-readonly: #{$ads-checkbox-border-color-readonly};

  // Cross

  --ads-checkbox-cross-color-checked: #{$ads-checkbox-cross-color-checked};
  --ads-checkbox-cross-color-hover: #{$ads-checkbox-cross-color-hover};
  --ads-checkbox-cross-color-disabled: #{$ads-checkbox-cross-color-disabled};
  --ads-checkbox-cross-color-readonly: #{$ads-checkbox-cross-color-readonly};

  // Label

  --ads-checkbox-label-color: #{$ads-checkbox-label-color};
  --ads-checkbox-label-color-hover: #{$ads-checkbox-label-color-hover};
  --ads-checkbox-label-color-required: #{$ads-checkbox-label-color-required};
}

:host([theme='dark']) .c-checkbox {
  // Background

  --ads-checkbox-background-color: #{$ads-checkbox-background-color-dark};
  --ads-checkbox-background-color-disabled: #{$ads-checkbox-background-color-disabled-dark};
  --ads-checkbox-background-color-readonly: #{$ads-checkbox-background-color-readonly-dark};

  // Border

  --ads-checkbox-border-color: #{$ads-checkbox-border-color-dark};
  --ads-checkbox-border-color-checked: #{$ads-checkbox-border-color-checked-dark};
  --ads-checkbox-border-color-disabled: #{$ads-checkbox-border-color-disabled-dark};
  --ads-checkbox-border-color-readonly: #{$ads-checkbox-border-color-readonly-dark};

  // Cross

  --ads-checkbox-cross-color-checked: #{$ads-checkbox-cross-color-checked-dark};
  --ads-checkbox-cross-color-hover: #{$ads-checkbox-cross-color-hover-dark};
  --ads-checkbox-cross-color-disabled: #{$ads-checkbox-cross-color-disabled-dark};
  --ads-checkbox-cross-color-readonly: #{$ads-checkbox-cross-color-readonly-dark};

  // Label

  --ads-checkbox-label-color: #{$ads-checkbox-label-color-dark};
  --ads-checkbox-label-color-hover: #{$ads-checkbox-label-color-hover-dark};
  --ads-checkbox-label-color-required: #{$ads-checkbox-label-color-required-dark};
}

:host([dir='ltr']) {
  @include ads-vars-component-checkbox-ltr;
}

:host([dir='rtl']) {
  @include ads-vars-component-checkbox-rtl;
}

:host(:first-child) {
  * {
    box-sizing: border-box;
  }
}

.c-checkbox {
  align-items: center;
  display: inline-flex;
  font-family: var(--ads-font-family);
  font-size: unset;
  margin-left: var(--ads-checkbox-margin-left, $ads-checkbox-margin-left-ltr);
  margin-right: var(--ads-checkbox-margin-right, $ads-checkbox-margin-right-ltr);

  &__box {
    cursor: pointer;
    display: inline-flex;
    flex-direction: row-reverse;
    margin: 5px 0;
  }

  &__cross {
    align-items: center;
    display: flex;
    height: var(--ads-checkbox-size-normal, $ads-checkbox-size-normal);
    justify-content: center;
    position: relative;
    top: 1px;
    width: var(--ads-checkbox-size-normal, $ads-checkbox-size-normal);

    &::before {
      background-color: var(--ads-checkbox-background-color, $ads-checkbox-background-color);
      border-color: var(--ads-checkbox-border-color, $ads-checkbox-border-color);
      border-radius: 2px;
      border-style: solid;
      border-width: $ads-checkbox-border-width;
      box-sizing: border-box;
      content: '';
      display: block;
      height: inherit;
      position: relative;
      width: inherit;
    }

    svg {
      display: none;
      height: 98%;
      position: absolute;
      width: 98%;
    }
  }

  &__box:not(.is-readonly):not(.is-disabled):hover &__cross,
  &__box.is-checked &__cross {
    svg {
      display: block;
    }
  }

  &__box:not(.is-readonly):not(.is-disabled):not(.is-checked) {
    > .c-checkbox__label:hover {
      color: var(--ads-checkbox-label-color-hover, $ads-checkbox-label-color-hover);
      fill: var(--ads-checkbox-label-color-hover, $ads-checkbox-label-color-hover);
    }

    &:hover .c-checkbox__cross {
      svg {
        fill: var(--ads-checkbox-cross-color-hover, $ads-checkbox-cross-color-hover);
      }
    }
  }

  &__box.is-checked .c-checkbox__cross {
    &::before {
      border-color: var(--ads-checkbox-border-color-checked, $ads-checkbox-border-color-checked);
    }

    svg {
      color: var(--ads-checkbox-cross-color-checked, $ads-checkbox-cross-color-checked);
      fill: var(--ads-checkbox-cross-color-checked, $ads-checkbox-cross-color-checked);
    }
  }

  &__box.is-readonly .c-checkbox__cross {
    &::before {
      background-color: var(--ads-checkbox-background-color-readonly, $ads-checkbox-background-color-readonly);
      border-color: var(--ads-checkbox-border-color-readonly, $ads-checkbox-border-color-readonly);
    }

    svg {
      fill: var(--ads-checkbox-cross-color-readonly, $ads-checkbox-cross-color-readonly);
    }
  }

  &__box.is-disabled .c-checkbox__cross {
    &::before {
      background-color: var(--ads-checkbox-background-color-disabled, $ads-checkbox-background-color-disabled);
      border-color: var(--ads-checkbox-border-color-disabled, $ads-checkbox-border-color-disabled);
    }

    svg {
      fill: var(--ads-checkbox-cross-color-disabled, $ads-checkbox-cross-color-disabled);
    }
  }

  &__box.is-readonly,
  &__box.is-disabled {
    cursor: default;
  }

  &__box > &__label {
    color: var(--ads-checkbox-label-color, $ads-checkbox-label-color);
    font-family: var(--ads-font-family);
    margin-left: var(--ads-checkbox-label-margin-left, $ads-checkbox-label-margin-left-ltr);
    margin-right: var(--ads-checkbox-label-margin-right, $ads-checkbox-label-margin-right-ltr);

    &:empty {
      margin-left: 0;
      margin-right: 0;
    }
  }

  &__box.is-reversed &__label:not(:empty) {
    text-align: right;
    // Variables flipped to support reversed variant
    margin-left: var(--ads-checkbox-label-margin-right, $ads-checkbox-label-margin-right-ltr);
    margin-right: var(--ads-checkbox-label-margin-left, $ads-checkbox-label-margin-left-ltr);
  }

  &__label.is-required::after {
    color: var(--ads-checkbox-label-color-required, $ads-checkbox-label-color-required);
    content: '*';
    margin: 0 2px;
  }

  &__box.is-small &__cross {
    height: var(--ads-checkbox-size-small, $ads-checkbox-size-small);
    top: 3px;
    width: var(--ads-checkbox-size-small, $ads-checkbox-size-small);
  }

  &__box:hover.is-small &__cross,
  &__box.is-small &__cross {
    &::before {
      height: var(--ads-checkbox-size-small, $ads-checkbox-size-small);
      width: var(--ads-checkbox-size-small, $ads-checkbox-size-small);
    }

    svg {
      font-size: var(--ads-checkbox-size-small, $ads-checkbox-size-small);
    }
  }

  &__input {
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }

  &__tooltip {
    height: auto;
    margin: 0 5px;
    width: auto;
  }

  &__helper {
    color: var(--arm-dark-grey);
    font-size: 0.7rem;
    margin: var(--ads-checkbox-helper-offset-margin, $ads-checkbox-helper-offset-margin-ltr);

    &.is-small {
      margin: var(--ads-checkbox-helper-offset-small-margin, $ads-checkbox-helper-offset-small-margin-ltr);
    }
  }
}
